/**
 * Wealth Snapshot Calculations
 * Functions for calculating wealth attribution, growth, and month-over-month changes
 */

import {
  calculateNetWorth,
  calculateTotalInvestments,
  calculateTotalCrypto,
  calculateTotalLiabilities,
  getInvestmentMarketValue
} from '../netWorthCalculations';
import {
  calculateAccountBalance,
  getAccountsWithBalances,
  groupInvestmentsByCategory
} from '../accountUtils';

/**
 * Calculate liability principal vs interest using amortization formula
 * @param {Object} liability - Liability object
 * @param {number} totalPayment - Total payment made this period
 * @returns {Object} Breakdown of principal and interest
 */
export const calculateLiabilityAttribution = (liability, totalPayment) => {
  if (!liability || !totalPayment) {
    return { principalPaid: 0, interestPaid: 0 };
  }

  const currentBalance = parseFloat(liability.currentBalance) || 0;
  const annualRate = parseFloat(liability.interestRate) || 0;

  // Calculate monthly interest charge
  const monthlyInterest = currentBalance * (annualRate / 100 / 12);

  // Principal is whatever remains after interest
  const principalPaid = Math.max(0, totalPayment - monthlyInterest);
  const interestPaid = Math.min(totalPayment, monthlyInterest);

  return {
    principalPaid,
    interestPaid,
    totalPayment
  };
};

/**
 * Calculate market growth for an account (residual method)
 * @param {Object} params
 * @returns {number} Market growth (positive or negative)
 */
export const calculateMarketGrowth = ({
  openingBalance,
  closingBalance,
  contributions = 0,
  withdrawals = 0,
  dividendsInterest = 0,
  fees = 0
}) => {
  // Market Growth = Change - Contributions + Withdrawals - Dividends + Fees
  const change = closingBalance - openingBalance;
  return change - contributions + withdrawals - dividendsInterest + fees;
};

/**
 * Calculate account-level attribution
 * @param {Object} account - Account object
 * @param {number} previousBalance - Balance at start of period
 * @param {number} currentBalance - Balance at end of period
 * @param {Object} userInputs - User-provided transaction data
 * @returns {Object} Account attribution breakdown
 */
export const calculateAccountAttribution = (
  account,
  previousBalance,
  currentBalance,
  userInputs = {}
) => {
  const contributions = parseFloat(userInputs.contributions) || 0;
  const withdrawals = parseFloat(userInputs.withdrawals) || 0;
  const dividendsInterest = parseFloat(userInputs.dividendsInterest) || 0;
  const fees = parseFloat(userInputs.fees) || 0;
  const investmentsSold = parseFloat(userInputs.investmentsSold) || 0;
  const tax = parseFloat(userInputs.tax) || 0;

  const netChange = currentBalance - previousBalance;

  const marketGrowth = calculateMarketGrowth({
    openingBalance: previousBalance,
    closingBalance: currentBalance,
    contributions,
    withdrawals,
    dividendsInterest,
    fees
  });

  return {
    accountId: account.id,
    accountName: account.name || account.accountName || 'Unknown Account',
    accountType: account.type || account.accountType || 'Unknown',
    accountPurpose: account.accountPurpose || 'taxable',

    openingBalance: previousBalance,
    closingBalance: currentBalance,
    netChange,

    contributions,
    withdrawals,
    marketGrowth,
    dividendsInterest,
    fees,
    investmentsSold,
    tax
  };
};

/**
 * Calculate liability attribution for the period
 * @param {Object} liability - Liability object
 * @param {number} previousBalance - Balance at start of period
 * @param {number} currentBalance - Balance at end of period
 * @param {Object} userInputs - User-provided payment data
 * @returns {Object} Liability attribution breakdown
 */
export const calculateLiabilityBreakdown = (
  liability,
  previousBalance,
  currentBalance,
  userInputs = {}
) => {
  const balanceReduction = previousBalance - currentBalance;

  // If user provided principal/interest split, use that
  if (userInputs.principalPaid !== undefined || userInputs.interestPaid !== undefined) {
    const principalPaid = parseFloat(userInputs.principalPaid) || 0;
    const interestPaid = parseFloat(userInputs.interestPaid) || 0;

    return {
      liabilityId: liability.id,
      name: liability.name,
      type: liability.type,
      openingBalance: previousBalance,
      closingBalance: currentBalance,
      totalPayment: principalPaid + interestPaid,
      principalPaid,
      interestPaid
    };
  }

  // Otherwise, calculate using amortization
  const attribution = calculateLiabilityAttribution(liability, balanceReduction);

  return {
    liabilityId: liability.id,
    name: liability.name,
    type: liability.type,
    openingBalance: previousBalance,
    closingBalance: currentBalance,
    totalPayment: balanceReduction,
    principalPaid: attribution.principalPaid,
    interestPaid: attribution.interestPaid
  };
};

/**
 * Calculate asset allocation breakdown
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Display currency code (e.g., 'USD', 'GBP')
 * @param {Object} exchangeRates - Exchange rates object with rates relative to EUR
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {Object} Asset class breakdown
 */
export const calculateAssetAllocation = (investments = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  const allocation = {
    stocks: 0,
    bonds: 0,
    realEstate: 0,
    commodities: 0,
    cryptocurrency: 0,
    cash: 0
  };

  investments.forEach(inv => {
    // Use getInvestmentMarketValue with live prices for consistent value calculation
    let amount = getInvestmentMarketValue(inv, priceMap);

    // ⭐ MULTI-CURRENCY: Convert to display currency if exchange rates available
    if (exchangeRates && exchangeRates.rates && displayCurrency) {
      const investmentCurrency = inv.currency || displayCurrency;
      if (investmentCurrency !== displayCurrency) {
        const fromRate = exchangeRates.rates[investmentCurrency];
        const toRate = exchangeRates.rates[displayCurrency];
        if (fromRate && toRate) {
          amount = (amount / fromRate) * toRate;
        }
      }
    }
    const category = (inv.category || '').toLowerCase();

    // Map investment categories to allocation buckets
    if (category.includes('stock') || category.includes('equity')) {
      allocation.stocks += amount;
    } else if (category.includes('bond') || category.includes('fixed')) {
      allocation.bonds += amount;
    } else if (category.includes('real estate') || category.includes('reit')) {
      allocation.realEstate += amount;
    } else if (category.includes('commodit') || category.includes('gold')) {
      allocation.commodities += amount;
    } else if (category.includes('crypto')) {
      allocation.cryptocurrency += amount;
    } else if (category.includes('cash') || category.includes('money market')) {
      allocation.cash += amount;
    } else {
      // Default unknown to stocks
      allocation.stocks += amount;
    }
  });

  return allocation;
};

/**
 * Calculate total wealth changes across all categories
 * @param {Object} currentSnapshot - Current period snapshot
 * @param {Object} previousSnapshot - Previous period snapshot (optional)
 * @returns {Object} Total change breakdown
 */
export const calculateTotalChanges = (currentSnapshot, previousSnapshot = null) => {
  // ✓ ALWAYS aggregate account-level data first (even for first snapshot)
  let newContributions = 0;
  let withdrawals = 0;
  let marketGrowth = 0;
  let dividends = 0;
  let investmentsSold = 0;
  let tax = 0;
  let charges = 0;

  (currentSnapshot.accounts || []).forEach(account => {
    newContributions += account.contributions || 0;
    withdrawals += account.withdrawals || 0;
    marketGrowth += account.marketGrowth || 0;
    dividends += account.dividendsInterest || 0;
    investmentsSold += account.investmentsSold || 0;
    tax += account.tax || 0;
    charges += account.fees || 0;
  });

  // Aggregate liability changes
  let liabilityReduction = 0;
  let interestPaid = 0;

  (currentSnapshot.liabilities || []).forEach(liability => {
    liabilityReduction += liability.principalPaid || 0;
    interestPaid += liability.interestPaid || 0;
  });

  // If no previous snapshot, this is the first snapshot
  if (!previousSnapshot) {
    return {
      totalChange: 0,
      totalChangePercent: 0,
      newContributions, // ✓ FIXED: Use aggregated value from accounts
      withdrawals: Math.abs(withdrawals),
      marketGrowth,
      liabilityReduction,
      interestPaid,
      dividends,
      investmentsSold,
      tax,
      charges,
      otherChanges: dividends // Keep for backward compatibility
    };
  }

  // Calculate changes from previous snapshot
  const currentNetWorth = currentSnapshot.netWorth || 0;
  const previousNetWorth = previousSnapshot.netWorth || 0;
  const totalChange = currentNetWorth - previousNetWorth;
  const totalChangePercent = previousNetWorth !== 0
    ? (totalChange / previousNetWorth) * 100
    : 0;

  return {
    totalChange,
    totalChangePercent,
    newContributions,
    withdrawals: Math.abs(withdrawals), // Make positive for display
    marketGrowth,
    liabilityReduction,
    interestPaid,
    dividends,
    investmentsSold,
    tax,
    charges,
    otherChanges: dividends // Keep for backward compatibility
  };
};

/**
 * Validate snapshot data before saving
 * @param {Object} snapshot - Snapshot object to validate
 * @returns {Object} Validation result { valid: boolean, errors: Array }
 */
export const validateSnapshot = (snapshot) => {
  const errors = [];
  const warnings = [];

  if (!snapshot.month) {
    errors.push('Month is required');
  }

  if (snapshot.netWorth === undefined || snapshot.netWorth === null) {
    errors.push('Net worth is required');
  }

  if (snapshot.totalAssets === undefined || snapshot.totalAssets === null) {
    errors.push('Total assets is required');
  }

  if (snapshot.totalLiabilities === undefined || snapshot.totalLiabilities === null) {
    errors.push('Total liabilities is required');
  }

  // Validate accounts array
  if (snapshot.accounts && !Array.isArray(snapshot.accounts)) {
    errors.push('Accounts must be an array');
  }

  // Validate liabilities array
  if (snapshot.liabilities && !Array.isArray(snapshot.liabilities)) {
    errors.push('Liabilities must be an array');
  }

  // ✓ CONTRIBUTION VALIDATION: Ensure aggregated contributions match sum of individual accounts
  if (snapshot.accounts && snapshot.changes) {
    const sumOfIndividualContributions = snapshot.accounts.reduce(
      (sum, acc) => sum + (acc.contributions || 0),
      0
    );
    const aggregatedContributions = snapshot.changes.newContributions || 0;

    // Allow small floating point differences (< 0.01)
    const difference = Math.abs(sumOfIndividualContributions - aggregatedContributions);
    if (difference > 0.01) {
      errors.push(
        `Contribution mismatch: Individual accounts sum to ${sumOfIndividualContributions.toFixed(2)} ` +
        `but aggregated total is ${aggregatedContributions.toFixed(2)}`
      );
    }

    // Validate no negative contributions
    snapshot.accounts.forEach(acc => {
      if (acc.contributions < 0) {
        errors.push(`Account "${acc.accountName}" has negative contributions (${acc.contributions})`);
      }
    });
  }

  // ✓ BACKWARD COMPATIBILITY: Warn if totalContributions is missing
  if (snapshot.changes && snapshot.changes.newContributions > 0 && !snapshot.totalContributions) {
    warnings.push('snapshot.totalContributions field is missing (will be auto-added for compatibility)');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Create a snapshot from current user data
 * @param {Object} params - User's current financial data
 * @returns {Object} Snapshot object ready to save
 */
export const createSnapshotFromCurrentData = ({
  investments = [],
  cryptoHoldings = [],
  liabilities = [],
  accounts = [],
  cashReserves = 0,
  userInputs = {}, // User-provided contribution/withdrawal data
  previousSnapshot = null,
  snapshotType = 'manual',
  notes = '',
  displayCurrency = null, // ⭐ Display currency for conversion
  exchangeRates = null,   // ⭐ Exchange rates for conversion
  priceMap = null         // ⭐ LIVE PRICING: Live prices from pricing service
}) => {
  // Calculate current net worth
  // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates and live prices
  const { totalAssets, totalLiabilities, netWorth } = calculateNetWorth({
    investments,
    cryptoHoldings,
    liabilities,
    cashReserves,
    displayCurrency,
    exchangeRates,
    priceMap
  });

  // Calculate asset allocation
  // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates and live prices
  const assetAllocation = calculateAssetAllocation(investments, displayCurrency, exchangeRates, priceMap);

  // Build account-level breakdown
  const accountsBreakdown = accounts.map(account => {
    // ⭐ MULTI-CURRENCY + LIVE PRICING: Pass exchange rates and live prices
    const currentBalance = calculateAccountBalance(account.id, investments, displayCurrency, exchangeRates, priceMap);

    const accountInputs = userInputs.accounts?.[account.id] || {};
    const contributions = parseFloat(accountInputs.contributions) || 0;
    const withdrawals = parseFloat(accountInputs.withdrawals) || 0;

    // ✓ FIXED: For first snapshot, calculate opening balance by subtracting contributions
    // This prevents showing false "market loss" when contributions are made
    const previousBalance = previousSnapshot
      ? (previousSnapshot.accounts?.find(a => a.accountId === account.id)?.closingBalance || 0)
      : (currentBalance - contributions + withdrawals); // First snapshot: work backwards from current balance

    return calculateAccountAttribution(
      account,
      previousBalance,
      currentBalance,
      accountInputs
    );
  });

  // Build liability breakdown
  const liabilitiesBreakdown = liabilities.map(liability => {
    const currentBalance = parseFloat(liability.currentBalance) || 0;

    const liabilityInputs = userInputs.liabilities?.[liability.id] || {};
    const principalPaid = parseFloat(liabilityInputs.principalPaid) || 0;

    // ✓ FIXED: For first snapshot, calculate opening balance by adding principal paid back
    // This correctly shows liability reduction when principal is paid
    const previousBalance = previousSnapshot
      ? (previousSnapshot.liabilities?.find(l => l.liabilityId === liability.id)?.closingBalance || 0)
      : (currentBalance + principalPaid); // First snapshot: opening was higher before principal payment

    return calculateLiabilityBreakdown(
      liability,
      previousBalance,
      currentBalance,
      liabilityInputs
    );
  });

  // Get current month in YYYY-MM format
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Build snapshot object
  const snapshot = {
    month,
    timestamp: now,
    netWorth,
    totalAssets,
    totalLiabilities,

    accounts: accountsBreakdown,
    assetAllocation,
    liabilities: liabilitiesBreakdown,

    snapshotType,
    notes,
    createdAt: now,
    createdBy: snapshotType === 'auto' ? 'system' : 'user'
  };

  // ✓ ALWAYS calculate changes (aggregation works for first snapshot too)
  // calculateTotalChanges() now properly aggregates contributions even without previousSnapshot
  snapshot.changes = calculateTotalChanges(snapshot, previousSnapshot);

  // ✓ BACKWARD COMPATIBILITY: Add totalContributions field to top-level snapshot
  // This mirrors the changes.newContributions value for older code that expects this field
  snapshot.totalContributions = snapshot.changes.newContributions || 0;
  snapshot.totalMarketGrowth = snapshot.changes.marketGrowth || 0;
  snapshot.totalWithdrawals = snapshot.changes.withdrawals || 0;
  snapshot.totalLiabilityReduction = snapshot.changes.liabilityReduction || 0;

  return snapshot;
};
